import {useContext, useEffect, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {getProducts} from "../api/catalog";
import {ProductInterface} from "../interfaces/catalog/ProductInterface";
import useLocalStorage from "./useLocalStorage";
import {LOCAL_STORAGE_KEY_VIEWED} from "../Config";
import {GlobalContext} from "../GlobalReducer";
import {ReducerStateInterface} from "../interfaces/core/ReducerStateInterface";

/**
 * Custom hook for managing viewed products
 * Tracks product views in localStorage and fetches viewed products data
 *
 * @param currentProductId - Optional ID of current product to exclude from viewed list
 * @returns Object containing viewed products array and loading state
 */
export default function useViewedProducts(currentProductId?: number) {
    const [state] = useContext<[ReducerStateInterface, Function]>(GlobalContext);
    const [viewedProductsId, setViewedProductsId] = useState<string[]>([]);

    const {
        addItem: addLocalStorageItem,
        getItems: getLocalStorageItems
    } = useLocalStorage();

    // Fetch viewed products from API
    const {data: responseViewedProducts} = useQuery({
        queryKey: [viewedProductsId, state.customer?.id],
        queryFn: () => getProducts({id: viewedProductsId}, state.customer),
        //@ts-ignore
        retry: !window.NOSCRIPT ? false : 1,
        enabled: viewedProductsId?.length > 0
    });

    // Track current product view and update viewed list
    useEffect(() => {
        if (currentProductId) {
            // Add current product to viewed history
            addLocalStorageItem(LOCAL_STORAGE_KEY_VIEWED, currentProductId.toString());

            // Get all viewed products except the current one
            setViewedProductsId(
                getLocalStorageItems(LOCAL_STORAGE_KEY_VIEWED)?.filter(
                    (productId: string) => productId !== currentProductId.toString()
                ) || []
            );
        } else {
            // If no current product, just load all viewed products
            setViewedProductsId(getLocalStorageItems(LOCAL_STORAGE_KEY_VIEWED) || []);
        }
    }, [currentProductId]);

    /**
     * Returns sorted list of viewed products based on view order
     */
    const getViewedProducts = (): ProductInterface[] => {
        if (!responseViewedProducts?.products?.length) {
            return [];
        }

        const localProducts = [...responseViewedProducts.products];

        // Sort products by the order they appear in viewedProductsId
        localProducts.sort((a, b) => {
            const aIndex = viewedProductsId.indexOf(a.id.toString());
            const bIndex = viewedProductsId.indexOf(b.id.toString());
            return aIndex - bIndex;
        });

        return localProducts;
    };

    return {
        viewedProducts: getViewedProducts(),
        isLoading: !responseViewedProducts && viewedProductsId.length > 0
    };
}
